import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchFigures, saveManualFigures } from "./accountLedgerService";
import { LEDGER_FIGURE_TOPICS, LEDGER_FIGURES, figureByPath } from "./ledgerFiguresDTO";
import { feedReport } from "../feedReport";

const g = (o, k) => GoldenSourceHelper.get(o, k);
const fmt = (v) => (v === null || v === undefined ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

/**
 * Enter figures manually — every Environmental reporting figure for the period,
 * showing what the mapped account codes already give automatically. A typed value
 * replaces the automated one; leave a box empty to use the automated value.
 */
export default function LedgerFiguresSection({ companyId, period, accent, refreshKey }) {
  const [rows, setRows] = useState({});
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const absorb = (list) => {
    const byPath = {};
    (list || []).forEach((r) => {
      byPath[g(r, "FieldPath")] = {
        autoValue: g(r, "AutoValue"), autoLines: g(r, "AutoLineCount") || 0, autoCodes: g(r, "AutoCodes") || "",
        manualValue: g(r, "ManualValue"),
      };
    });
    setRows(byPath);
    setDraft(Object.fromEntries(Object.entries(byPath).map(([p, r]) => [p, r.manualValue === null || r.manualValue === undefined ? "" : String(Number(r.manualValue))])));
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    setError("");
    try { absorb(await fetchFigures(companyId, period.from, period.to)); } catch (e) { setError(e.message || "Could not load the figures."); }
  }, [companyId, period.from, period.to]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const save = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const payload = LEDGER_FIGURES.map((f) => ({ path: f.path, value: draft[f.path] ?? "", unit: f.unit }));
      const list = await saveManualFigures(companyId, period.from, period.to, payload);
      absorb(list);
      // every figure with a value (typed wins, else automated) goes into the report store
      const byTopic = {};
      (list || []).forEach((r) => {
        const path = g(r, "FieldPath");
        const manual = g(r, "ManualValue");
        const auto = g(r, "AutoValue");
        const hasManual = manual !== null && manual !== undefined;
        const v = hasManual ? manual : auto;
        if (v === null || v === undefined || !path) return;
        const [topicKey, ...rest] = path.split(".");
        (byTopic[topicKey] = byTopic[topicKey] || []).push({
          path: rest.join("."),
          value: String(Number(v)),
          unit: figureByPath(path)?.unit || null,
          sourceCode: "LEDGER",
          sourceRef: hasManual
            ? "Accounts & Ledger · entered figure"
            : `Accounts & Ledger · codes ${g(r, "AutoCodes") || "?"} · ${g(r, "AutoLineCount") || 0} ledger lines`,
        });
      });
      const fed = await feedReport(companyId, period.from, period.to,
        Object.entries(byTopic).map(([topicKey, values]) => ({ topicKey, pillarCode: "E", values })));
      setNotice(`Figures saved. ${fed}`);
    } catch (e) {
      setError(e.message || "Could not save the figures.");
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => ({
    auto: Object.values(rows).filter((r) => r.autoValue !== null && r.autoValue !== undefined).length,
    manual: Object.values(rows).filter((r) => r.manualValue !== null && r.manualValue !== undefined).length,
  }), [rows]);

  return (
    <>
      <p className="small text-muted">
        Figures for <strong>{period.from} → {period.to}</strong>. <strong>{counts.auto}</strong> come automatically from your
        mapped account codes; <strong>{counts.manual}</strong> are typed in. A typed value replaces the automated one — leave a
        box empty to use the automated value.
      </p>
      {error && <div className="alert alert-danger small">{error}</div>}
      {notice && <div className="alert alert-success small">{notice}</div>}

      {LEDGER_FIGURE_TOPICS.map((t) => (
        <div key={t.key} className="mb-3">
          <div className="extra-small fw-bold text-uppercase mb-2" style={{ color: accent }}>{t.name}</div>
          <div className="row g-2">
            {t.figures.map((f) => {
              const r = rows[f.path];
              const hasAuto = r && r.autoValue !== null && r.autoValue !== undefined;
              return (
                <div key={f.path} className="col-md-4 col-lg-3">
                  <label className="form-label extra-small fw-bold text-muted mb-1" htmlFor={`lf-${f.path}`} title={f.groupLabel}>
                    {f.label}
                  </label>
                  <div className="input-group input-group-sm">
                    <input id={`lf-${f.path}`} type="number" inputMode="decimal" className="form-control"
                      placeholder={hasAuto ? fmt(r.autoValue) : ""}
                      value={draft[f.path] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [f.path]: e.target.value }))} />
                    {f.unit && <span className="input-group-text">{f.unit}</span>}
                  </div>
                  {hasAuto && (
                    <div className="extra-small text-success mt-1">
                      Automated {fmt(r.autoValue)} {f.unit} · codes {r.autoCodes} · {r.autoLines} line{r.autoLines === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <button type="button" className="btn btn-sm text-white fw-bold" style={{ backgroundColor: accent }} disabled={busy} onClick={save}>
        {busy ? "Saving…" : "Save figures"}
      </button>
    </>
  );
}
